import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";

import { EventRecord } from "../EventRecord";

import { eventQueryKeys } from "./eventQueryKeys";

/**
 * Bulk-delete events in a single request to the backend bulk-delete endpoint.
 *
 * Use this anywhere multiple events are selected and deleted together. The
 * deletion is scoped to the current org. On success, every event list view is
 * marked stale so the removed events disappear.
 */
export function useDeleteEvents() {
  const queryClient = useQueryClient();
  const { events } = useDomainServices();
  const orgId = useCurrentOrgId();

  return useMutation<void, Error, EventRecord["event_id"][]>({
    mutationFn: (eventIds) =>
      events.deleteEvents(eventIds, { resourceOwnerId: orgId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: eventQueryKeys.lists(orgId),
      });
      void queryClient.invalidateQueries({
        queryKey: eventQueryKeys.forWorkspaces(orgId),
        refetchType: "none",
      });
    },
  });
}
